import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card, CardContent, Typography, Box,
    IconButton, Avatar, Stack,
    Divider, Chip, Grid, Badge
} from '@mui/material';
import { Favorite, FavoriteBorder, AccessTime, Email as EmailIcon, Phone as PhoneIcon, VerifiedUser as VerifiedIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { toggleFavoriteUser } from '../../services/api';
import { formatLastLoginTime } from '../../utils/dateUtils';

// 入居希望時期の日本語変換マップ
const MOVE_IN_PREFERENCE_MAP = {
    'within 1 month': '1ヶ月以内',
    'within 3 months': '3ヶ月以内',
    'within 6 months': '6ヶ月以内',
    'more than 6 months': '6ヶ月以上'
};

function UserCard({ user }) {
    const navigate = useNavigate();
    const { currentUser, userProfile } = useAuth();
    const { showNotification } = useNotification();
    const [isFavorite, setIsFavorite] = useState(false);

    useEffect(() => {
        if (userProfile?.favorites?.users) {
            setIsFavorite(userProfile.favorites.users.includes(user._id));
        }
    }, [userProfile?.favorites?.users, user._id]);

    const handleClick = () => {
        navigate(`/users/${user._id}`);
    };

    const handleFavoriteClick = async (e) => {
        e.stopPropagation();
        if (!currentUser) {
            showNotification('お気に入り追加にはログインが必要です', 'warning');
            return;
        }

        try {
            await toggleFavoriteUser(user._id);
            setIsFavorite(!isFavorite);
            showNotification(
                isFavorite ? 'お気に入りから削除しました' : 'お気に入りに追加しました',
                'success'
            );
        } catch (error) {
            showNotification('お気に入りの更新に失敗しました', 'error');
        }
    };

    const profile = user.profile;
    // 年齢計算
    const age = profile.dateOfBirth ?
        Math.floor((new Date() - new Date(profile.dateOfBirth)) / (365.25 * 24 * 60 * 60 * 1000)) :
        null;

    const preferredArea = profile.preferences?.preferredAreas?.[0] || {};

    // 希望エリアのテキスト生成
    const getAreaText = () => {
        const parts = [];
        if (preferredArea.prefecture) parts.push(preferredArea.prefecture);
        if (preferredArea.city) parts.push(preferredArea.city);
        return parts.join(' ');
    };

    return (
        <Card
            onClick={handleClick}
            sx={{
                cursor: 'pointer',
                border: '1px solid rgba(0, 0, 0, 0.12)',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                },
                position: 'relative',
                transition: 'all 0.3s ease',
                backgroundColor: '#fff'
            }}
        >
            {/* お気に入りボタン */}
            <IconButton
                onClick={handleFavoriteClick}
                sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    zIndex: 2,
                    bgcolor: 'background.paper',
                    '&:hover': { bgcolor: 'background.paper' }
                }}
            >
                {isFavorite ? <Favorite color="error" /> : <FavoriteBorder />}
            </IconButton>

            {/* サンプルユーザーバッジ */}
            {user.isSample && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 8,
                        left: 8,
                        zIndex: 2,
                    }}
                >
                    <Chip
                        label="サンプル"
                        size="small"
                        color="primary"
                        sx={{
                            fontSize: '0.7rem',
                            height: '20px',
                            backgroundColor: 'rgba(25, 118, 210, 0.8)',
                            color: 'white'
                        }}
                    />
                </Box>
            )}

            <CardContent>
                {/* 上部: プロフィール基本情報 */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar
                        src={profile.profileImage}
                        sx={{ width: 60, height: 60, mr: 2 }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flexWrap: 'nowrap',
                            minWidth: 0,
                            overflow: 'hidden',
                            pr: { xs: 5, sm: 6 }
                        }}>
                            <Typography
                                variant="h6"
                                component="div"
                                sx={{
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                }}
                            >
                                {profile.nickname}
                            </Typography>
                        </Box>
                        <Grid container spacing={1} sx={{ mt: 0.5 }}>
                            <Grid item>
                                <Typography
                                    color="textSecondary"
                                    variant="body2"
                                    sx={{
                                        whiteSpace: 'nowrap',
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}
                                >
                                    {age}歳
                                </Typography>
                            </Grid>
                            <Grid item>
                                <Typography
                                    color="textSecondary"
                                    variant="body2"
                                    sx={{
                                        whiteSpace: 'nowrap',
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}
                                >
                                    {profile.gender === 'male' ? '男性' : '女性'}
                                </Typography>
                            </Grid>
                            <Grid item>
                                <Typography
                                    color="textSecondary"
                                    variant="body2"
                                    sx={{
                                        whiteSpace: 'nowrap',
                                        display: 'flex',
                                        alignItems: 'center',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    }}
                                >
                                    {profile.occupation || '職業未設定'}
                                </Typography>
                            </Grid>
                        </Grid>

                        {/* 認証バッジ */}
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                            {user.emailVerified && (
                                <Chip
                                    icon={<EmailIcon />}
                                    label="メール認証"
                                    color="success"
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                        borderRadius: 2,
                                        fontSize: '0.7rem',
                                        height: '20px'
                                    }}
                                />
                            )}
                            {user.phoneVerified && (
                                <Chip
                                    icon={<PhoneIcon />}
                                    label="電話認証"
                                    color="success"
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                        borderRadius: 2,
                                        fontSize: '0.7rem',
                                        height: '20px'
                                    }}
                                />
                            )}
                        </Box>

                        {formatLastLoginTime(user.lastLoginAt) && (
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                mt: 0.5
                            }}>
                                <AccessTime sx={{ fontSize: '0.875rem', color: 'text.secondary' }} />
                                <Typography color="textSecondary" variant="body2" sx={{ fontSize: '0.75rem' }}>
                                    {formatLastLoginTime(user.lastLoginAt)}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>

                {/* 自己紹介 */}
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                        mb: 2,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        whiteSpace: 'pre-line'
                    }}
                >
                    {profile.introduction || '自己紹介文が未設定です'}
                </Typography>

                <Divider sx={{ my: 1.5 }} />

                {/* 下部: 希望条件 */}
                <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        希望家賃: ¥{profile.preferences?.rentRange?.min?.toLocaleString() || 0}
                        ~ ¥{profile.preferences?.rentRange?.max?.toLocaleString() || 0}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                            size="small"
                            label={MOVE_IN_PREFERENCE_MAP[profile.moveInPreference] || '時期未定'}
                        />
                        <Chip
                            size="small"
                            label={getAreaText() || '希望エリア未設定'}
                        />
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
}

export default UserCard; 